import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';

interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
}

/**
 * Mirrors the user pool's password policy (infrastructure/lib/stacks/auth-stack.ts)
 * so users see what's missing before Cognito rejects the attempt.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  { label: 'At least 12 characters', test: p => p.length >= 12 },
  { label: 'An uppercase letter',    test: p => /[A-Z]/.test(p) },
  { label: 'A lowercase letter',     test: p => /[a-z]/.test(p) },
  { label: 'A number',               test: p => /[0-9]/.test(p) },
  { label: 'A symbol',               test: p => /[^A-Za-z0-9]/.test(p) },
];

/**
 * Step 2 of password recovery: enter the emailed code and a new password.
 *
 * Reached from /forgot-password, or straight from /login when an admin has
 * reset the account (`?reason=admin`) — in that case Cognito has already
 * emailed the code.
 */
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent implements OnInit {
  email = '';
  code = '';
  newPassword = '';
  confirmPassword = '';
  error: string | null = null;
  notice: string | null = null;
  loading = false;
  resending = false;
  adminReset = false;

  readonly rules = PASSWORD_RULES;

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.email = params.get('email') ?? '';
    this.adminReset = params.get('reason') === 'admin';
  }

  passes(rule: PasswordRule): boolean {
    return rule.test(this.newPassword);
  }

  async onSubmit(): Promise<void> {
    const validationError = this.validate();
    if (validationError) {
      this.error = validationError;
      return;
    }

    this.error = null;
    this.notice = null;
    this.loading = true;
    try {
      const email = this.email.trim();
      await this.auth.confirmPasswordReset(email, this.code.trim(), this.newPassword);
      await this.router.navigate(['/login'], { queryParams: { email, reset: 'success' } });
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : 'Could not reset your password. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  async resendCode(): Promise<void> {
    const email = this.email.trim();
    if (!email) {
      this.error = 'Enter your email address first.';
      return;
    }

    this.error = null;
    this.notice = null;
    this.resending = true;
    try {
      await this.auth.requestPasswordReset(email);
      this.notice = `If an account exists for ${email}, a new code is on its way.`;
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : 'Could not send a new code. Please try again.';
    } finally {
      this.resending = false;
    }
  }

  private validate(): string | null {
    if (!this.email.trim()) return 'Enter the email address for your account.';
    if (!this.code.trim()) return 'Enter the code from your email.';
    if (!this.rules.every(r => r.test(this.newPassword))) {
      return 'Your new password does not meet all of the requirements below.';
    }
    if (this.newPassword !== this.confirmPassword) return 'The passwords do not match.';
    return null;
  }
}
